/**
 * Intro picture-tier vignettes (AUTHORING.md v3): one painted background per picture (public/vignettes/intro/<bg>.webp,
 * prompts docs/vignette-prompts.py, CREDITS.md) + the picture's posed object from core (`content/worlds/intro/pictures.ts`).
 * `elements` = every part the painting shows (each one is drawn + coloured in the picture lesson).
 */
import type { VignetteSpec, WorldVignettes } from '../../vignette/types';

const v = (id: string, bg: string, elements: string[], overlay?: string[]): VignetteSpec => ({ id: `${id}-picture`, bg, elements, ...(overlay ? { overlay } : {}) });

const specs: VignetteSpec[] = [
  /* L1 */
  v('intro-sunny-circle', 'intro-sunny-circle', ['sky', 'ground', 'hills', 'daisies']),
  v('intro-pattern-snake', 'intro-pattern-snake', ['sky', 'ground', 'stones', 'flowers', 'pot']),
  v('intro-remix-sun-shades', 'intro-remix-sun', ['sky', 'ground', 'sea', 'towel', 'bucket']),
  v('intro-remix-sun-sleepy', 'doodle-star', ['sky', 'ground', 'clouds', 'stars', 'roofs']),
  v('intro-remix-sun-flower', 'intro-remix-sun-flower', ['sky', 'ground', 'fence', 'tulips']),
  /* L2 */
  v('intro-cozy-house', 'intro-cozy-house', ['sky', 'clouds', 'ground', 'path', 'trees']),
  v('intro-hot-cocoa', 'intro-hot-cocoa', ['sky', 'ground', 'window', 'placemat']),
  v('intro-alarm-clock', 'intro-alarm-clock', ['sky', 'ground', 'curtains', 'books']),
  v('intro-remix-house-candy', 'intro-remix-house', ['sky', 'ground', 'trees', 'mushrooms', 'ferns']),
  v('intro-remix-house-tree', 'intro-remix-house-tree', ['sky', 'ground', 'pond', 'reeds', 'stones']),
  v('intro-remix-house-mushroom', 'intro-remix-house-mushroom', ['sky', 'ground', 'pines', 'log']),
  /* L3 */
  v('intro-kite', 'intro-kite', ['sky', 'clouds', 'ground', 'wheat']),
  v('intro-sailboat', 'intro-sailboat', ['sky', 'ground', 'sea', 'island', 'bushes']),
  v('intro-hot-air-balloon', 'intro-hot-air-balloon', ['sky', 'fields', 'river']),
  v('intro-remix-kite-fish', 'intro-remix-kite', ['sky', 'ground', 'dunes', 'duneGrass']),
  v('intro-remix-kite-butterfly', 'intro-colour-balloon', ['sky', 'clouds', 'hills', 'ground']),
  v('intro-remix-kite-octopus', 'intro-remix-kite-octopus', ['sky', 'ground', 'sea', 'rocks', 'shells']),
  v('intro-remix-boat-paper', 'intro-remix-boat', ['sky', 'sunflowers', 'ground', 'sea', 'pier', 'buoys']),
  v('intro-remix-boat-patched', 'intro-remix-boat-patched', ['sky', 'ground', 'banks', 'river', 'bridge', 'reeds']),
  v('intro-remix-boat-fishing', 'intro-remix-boat-fishing', ['sky', 'mountains', 'ground', 'sea', 'pines']),
  /* L4 */
  v('intro-shaded-ball', 'intro-shaded-ball', ['sky', 'ground', 'hedge', 'trees', 'bench']),
  v('intro-rocket', 'intro-rocket', ['sky', 'moon', 'stars', 'ground', 'plants']),
  v('intro-desk-lamp', 'intro-desk-lamp', ['sky', 'ground', 'window', 'plant', 'brushJar']),
  v('intro-remix-rocket-candy-stripe', 'intro-remix-rocket', ['sky', 'nebula', 'planet', 'moon', 'ground']),
  v('intro-remix-rocket-pet', 'intro-colour-rocket', ['sky', 'moon', 'earth', 'stars', 'ground']),
  v('intro-remix-rocket-submarine', 'intro-remix-rocket-submarine', ['sky', 'ground', 'seaweed', 'coral', 'bubbles']),
  v('intro-remix-ball-beach', 'intro-remix-ball', ['sky', 'sun', 'ground', 'bushes', 'pool', 'bubbles']),
  v('intro-remix-ball-snow-globe', 'intro-colour-cocoa', ['sky', 'sunflowers', 'ground', 'armchair', 'lampTable']),
  v('intro-remix-ball-bubble', 'intro-doodle-shaded-shapes', ['sky', 'sun', 'sunflowers', 'ground']),
  /* playground-only */
  v('intro-balloon-bunch', 'intro-then-now-sun', ['sky', 'ground', 'sea', 'cliffs']),
];

export const INTRO_VIGNETTES: WorldVignettes = { world: 'intro', specs };
